import Vue from 'vue'
import axios from 'axios'
import { toastError } from '@/libs/toastification'

/**
 * Composable for fulfillment fee recalculation.
 * Handles payload construction, request cancellation (race condition prevention),
 * debounced requests, error handling, and retries.
 *
 * @param {Object} vm - Vue component instance
 * @returns {Object} Recalculation methods and reactive state helpers
 */
export default function useRecalculateFulfillment(vm) {
  const state = Vue.observable({
    loadingMap: {},
    errorMap: {},
    lastParamsMap: {},
  })

  const cancelTokens = new Map()
  const debounceTimers = new Map()

  /**
   * Resolve warehouse ID from Vuex store profile or dispatch profile fetch.
   * @returns {Promise<number|null>}
   */
  const getWarehouseId = async () => {
    if (vm.$store?.state?.warehouse?.profile?.id) {
      return vm.$store.state.warehouse.profile.id
    }
    try {
      const data = await vm.$store.dispatch('warehouse/getWarehouseProfile')
      return data?.id || null
    } catch (e) {
      return null
    }
  }

  /**
   * Build recalculate payload matching the structure required by /komship/api/v2/orders/recalculate-fulfillment.
   * @param {Object} params
   * @param {Object} params.order - Order object
   * @param {Array} params.packings - Selected packings array
   * @param {number} [params.totalOrder] - Total valid order count
   * @param {boolean} [params.fromMarketplace=false] - Whether order originates from marketplace
   * @returns {Object}
   */
  const buildPayload = ({
    order = {}, packings = [], totalOrder, fromMarketplace = false,
  }) => {
    const validPackings = (packings || []).filter(p => p && (p.id || p.option_id) && (p.layer > 0 || p.total_layer > 0))
    const mappedPackings = validPackings.map(val => ({
      id: fromMarketplace ? (val.id || val.option_id) : (val.option_id || val.id),
      total_layer: Number(val.layer || val.total_layer || 1),
    }))

    const products = (order.products || []).map(val => ({
      product_id: val.product_id,
      total_item: fromMarketplace ? val.quantity : (val.total_item || 1),
      is_product_bonus: Boolean(val.is_product_bonus),
      is_bundling: Boolean(val.is_bundling),
      bundle_components: (val.product_bundles || []).map(bundle => ({
        product_id: bundle.product_id,
        qty: bundle.qty || 1,
        is_product_bonus: Boolean(bundle.is_product_bonus),
      })),
    }))

    return {
      total_order: totalOrder !== undefined ? totalOrder : (order.total_order || 1),
      packings: mappedPackings,
      products,
    }
  }

  /**
   * Execute recalculation immediately with cancellation of any previous in-flight request for the same orderKey.
   * @param {Object} params
   * @returns {Promise<Object|null>}
   */
  const recalculate = async ({
    order,
    packings,
    totalOrder,
    orderKey,
    fromMarketplace = false,
    onSuccess,
    onError,
  }) => {
    const key = orderKey !== undefined ? orderKey : (order?.outbound_detail_id || 'default')

    // Cancel existing request for this orderKey to avoid race conditions
    if (cancelTokens.has(key)) {
      cancelTokens.get(key).cancel('Superseded by newer recalculation')
      cancelTokens.delete(key)
    }

    const source = axios.CancelToken.source()
    cancelTokens.set(key, source)

    Vue.set(state.loadingMap, key, true)
    Vue.set(state.errorMap, key, null)
    Vue.set(state.lastParamsMap, key, {
      order,
      packings,
      totalOrder,
      orderKey: key,
      fromMarketplace,
      onSuccess,
      onError,
    })

    try {
      const warehouseId = await getWarehouseId()
      const payload = buildPayload({
        order, packings, totalOrder, fromMarketplace,
      })

      const response = await vm.$http_new.post(
        '/komship/api/v2/orders/recalculate-fulfillment',
        { ...payload, warehouse_id: warehouseId },
        { cancelToken: source.token },
      )

      const result = response.data?.data || {}
      result.fulfillment_cost = result.grand_fulfillment_cost || result.fulfillment_cost || 0

      Vue.set(state.loadingMap, key, false)
      Vue.set(state.errorMap, key, null)
      cancelTokens.delete(key)

      if (typeof onSuccess === 'function') {
        onSuccess(result)
      }
      return result
    } catch (err) {
      if (axios.isCancel(err)) {
        return null
      }
      Vue.set(state.loadingMap, key, false)
      Vue.set(state.errorMap, key, err)
      cancelTokens.delete(key)

      toastError('Gagal menghitung biaya fulfillment, silahkan coba lagi')
      if (typeof onError === 'function') {
        onError(err)
      }
      throw err
    }
  }

  /**
   * Schedule debounced recalculation. Cancels any pending timer for the orderKey.
   * @param {Object} params
   * @param {number} [params.delay=500] - Debounce delay in milliseconds
   */
  const debouncedRecalculate = ({
    order,
    packings,
    totalOrder,
    orderKey,
    delay = 500,
    fromMarketplace = false,
    onSuccess,
    onError,
  }) => {
    const key = orderKey !== undefined ? orderKey : (order?.outbound_detail_id || 'default')

    if (debounceTimers.has(key)) {
      clearTimeout(debounceTimers.get(key))
    }

    Vue.set(state.loadingMap, key, true)
    Vue.set(state.errorMap, key, null)
    Vue.set(state.lastParamsMap, key, {
      order,
      packings,
      totalOrder,
      orderKey: key,
      fromMarketplace,
      onSuccess,
      onError,
    })

    const timer = setTimeout(() => {
      debounceTimers.delete(key)
      recalculate({
        order,
        packings,
        totalOrder,
        orderKey: key,
        fromMarketplace,
        onSuccess,
        onError,
      }).catch(() => {})
    }, delay)

    debounceTimers.set(key, timer)
  }

  /**
   * Retry the last attempted recalculation for an orderKey.
   * @param {string|number} orderKey
   * @returns {Promise<Object|null>}
   */
  const retryRecalculate = async orderKey => {
    const lastParams = state.lastParamsMap[orderKey]
    if (!lastParams) return null
    return recalculate(lastParams)
  }

  /**
   * Check if a specific order or any order is recalculating.
   * @param {string|number} [orderKey]
   * @returns {boolean}
   */
  const isRecalculating = orderKey => {
    if (orderKey !== undefined) {
      return Boolean(state.loadingMap[orderKey])
    }
    return Object.values(state.loadingMap).some(Boolean)
  }

  /**
   * Check if an order has a recalculation error.
   * @param {string|number} orderKey
   * @returns {boolean}
   */
  const hasError = orderKey => Boolean(state.errorMap[orderKey])

  /**
   * Check if any order currently has an error.
   * @returns {boolean}
   */
  const isAnyError = () => Object.values(state.errorMap).some(Boolean)

  /**
   * Cleanup all active timers and cancel in-flight HTTP requests.
   */
  const cleanup = () => {
    debounceTimers.forEach(timer => clearTimeout(timer))
    debounceTimers.clear()
    cancelTokens.forEach(source => source.cancel('Component destroyed'))
    cancelTokens.clear()
  }

  return {
    state,
    getWarehouseId,
    buildPayload,
    recalculate,
    debouncedRecalculate,
    retryRecalculate,
    isRecalculating,
    hasError,
    isAnyError,
    cleanup,
  }
}
